import { Capacitor } from '@capacitor/core';

/**
 * Authentification biométrique (empreinte / Face ID) — wrapper au-dessus de
 * @aparajita/capacitor-biometric-auth.
 *
 * Sur le web ou lorsque l'appareil n'a pas de biométrie configurée, le verrou
 * est « ouvert » par défaut (retourne true) : la protection est une couche
 * native bonus, jamais un blocage sur navigateur.
 */

const isNative = Capacitor.isNativePlatform();

/** Indique si une biométrie est disponible et configurée sur l'appareil. */
export const isBiometricAvailable = async () => {
  if (!isNative) return false;
  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    const info = await BiometricAuth.checkBiometry();
    return Boolean(info?.isAvailable);
  } catch {
    return false;
  }
};

/**
 * Demande une vérification biométrique.
 * @returns {Promise<boolean>} true si authentifié (ou si la biométrie n'est
 *          pas applicable sur cette plateforme), false si l'utilisateur a
 *          échoué/annulé.
 */
export const verifyBiometric = async (reason = 'Confirmez votre identité') => {
  if (!isNative) return true; // pas de gate sur le web
  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    const info = await BiometricAuth.checkBiometry();
    if (!info?.isAvailable) return true; // aucun capteur configuré → on laisse passer
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: 'Annuler',
      allowDeviceCredential: true,
      iosFallbackTitle: 'Utiliser le code',
      androidTitle: 'Vérification requise',
      androidSubtitle: reason,
    });
    return true; // authenticate() rejette si échec/annulation
  } catch (e) {
    console.warn('Échec biométrie:', e?.message);
    return false;
  }
};
